import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Icon, X, ArrowLeft, ArrowRight, Check,
  Sparkles, CalendarDays, MapPin, Map, Ticket, DollarSign,
  Route, Wallet, Luggage, Info, BookOpen, Image, Upload,
  type LucideIcon,
} from '../ui/Icon';
import type { Trip } from '../../types';
import { hidesTab } from '../../utils/tripPermissions';

interface TripTourProps {
  tripId: string;
  trip: Pick<Trip, 'viewerIsAgency'> | null;
  onClose: () => void;
}

interface StepDef {
  key: string;          // i18n suffix under trip.tour.steps.*
  target?: string;      // data-tour value to spotlight; absent → centered card
  route?: string;       // sub-route to open for this step
  clickTarget?: string; // data-tour value to click on entry (to change the view)
  NavIcon: LucideIcon;
}

// Steps mix always-mounted nav anchors (stable spotlight) with in-tab anchors
// that demonstrate specific features. `clickTarget` lets a step drive the app —
// e.g. switch the map into the routes view so the user sees it live.
const STEPS: StepDef[] = [
  { key: 'welcome',     NavIcon: Sparkles },
  { key: 'importDoc',   target: 'import-doc', NavIcon: Upload },
  { key: 'itinerary',   target: 'nav-itinerary', route: 'itinerary', NavIcon: CalendarDays },
  { key: 'itineraryAi', target: 'itinerary-ai',  route: 'itinerary', NavIcon: Sparkles },
  { key: 'places',      target: 'nav-venues',    route: 'venues',    NavIcon: MapPin },
  { key: 'map',         target: 'nav-map',       route: 'map',       NavIcon: Map },
  { key: 'mapRoutes',   target: 'map-modes', clickTarget: 'map-mode-routes',    route: 'map', NavIcon: Route },
  { key: 'bookings',    target: 'nav-bookings',  route: 'bookings',  NavIcon: Ticket },
  { key: 'expenses',    target: 'nav-expenses',  route: 'expenses',  NavIcon: DollarSign },
  { key: 'balances',    target: 'expenses-balances', route: 'expenses', NavIcon: Wallet },
  { key: 'packing',     target: 'nav-packing', route: 'packing', NavIcon: Luggage },
  { key: 'tips',        target: 'nav-info',    route: 'info',    NavIcon: Info },
  { key: 'notes',       target: 'nav-notes',   route: 'notes',   NavIcon: BookOpen },
  { key: 'gallery',     target: 'nav-gallery', route: 'gallery', NavIcon: Image },
  { key: 'finish',      NavIcon: Check },
];

type Placement = 'center' | 'top' | 'bottom' | 'left' | 'right';

function findVisibleTarget(name: string): HTMLElement | null {
  const els = Array.from(document.querySelectorAll<HTMLElement>(`[data-tour="${name}"]`));
  // Note: can't use offsetParent to test visibility — it's always null for
  // position:fixed elements (e.g. the itinerary AI button), which would wrongly
  // exclude them. Check the box size + computed style instead.
  return (
    els.find(el => {
      const r = el.getBoundingClientRect();
      if (r.width <= 0 || r.height <= 0) return false;
      const cs = getComputedStyle(el);
      return cs.visibility !== 'hidden' && cs.display !== 'none';
    }) ?? null
  );
}

export default function TripTour({ tripId, trip, onClose }: TripTourProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // ADR-0038: no stop at a tab the agency is not shown.
  const steps = useMemo(() => STEPS.filter(s => !trip || !s.route || !hidesTab(trip, s.route)), [trip]);

  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number; placement: Placement }>({
    top: 0, left: 0, placement: 'center',
  });
  const [win, setWin] = useState({ w: window.innerWidth, h: window.innerHeight });

  const cardRef = useRef<HTMLDivElement>(null);
  const indexRef = useRef(index);
  indexRef.current = index;

  const step = steps[index];
  const isLast = index === steps.length - 1;

  // Read the current step's spotlight target; returns false if not found yet.
  const measure = useCallback(() => {
    const s = steps[indexRef.current];
    if (!s.target) { setRect(null); return true; }
    const el = findVisibleTarget(s.target);
    if (el) { setRect(el.getBoundingClientRect()); return true; }
    return false;
  }, [steps]);

  // Lock scroll + track viewport size + Escape to skip.
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onResize = () => { setWin({ w: window.innerWidth, h: window.innerHeight }); measure(); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', measure, true);
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('keydown', onKey);
    };
  }, [measure, onClose]);

  // On step change: navigate to the tab, optionally drive the view via a
  // clickTarget, then poll until the spotlight anchor is laid out.
  useEffect(() => {
    if (step.route) navigate(`/trips/${tripId}/${step.route}`);
    // Clear any previous spotlight so a not-yet-mounted target never shows a
    // stale ring on the wrong element; the poll below fills it back in.
    setRect(null);
    let raf = 0;
    let clicked = !step.clickTarget;
    const start = performance.now();
    const tick = () => {
      if (!clicked) {
        const c = findVisibleTarget(step.clickTarget!);
        if (c) { c.click(); clicked = true; }
      }
      const found = measure();
      if ((!found || !clicked) && performance.now() - start < 2500) {
        raf = requestAnimationFrame(tick);
      }
    };
    tick();
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  // Position the tooltip card once we know both the target rect and card size.
  useLayoutEffect(() => {
    const card = cardRef.current;
    if (!card) return;
    const cw = card.offsetWidth;
    const ch = card.offsetHeight;
    const { w: vw, h: vh } = win;
    const GAP = 16;
    const M = 12;

    if (!rect) {
      setPos({ top: (vh - ch) / 2, left: (vw - cw) / 2, placement: 'center' });
      return;
    }

    let placement: Placement;
    let top: number;
    let left: number;
    if (rect.right + GAP + cw <= vw - M) {
      placement = 'right'; left = rect.right + GAP; top = rect.top + rect.height / 2 - ch / 2;
    } else if (rect.left - GAP - cw >= M) {
      placement = 'left'; left = rect.left - GAP - cw; top = rect.top + rect.height / 2 - ch / 2;
    } else if (rect.top - GAP - ch >= M) {
      placement = 'top'; top = rect.top - GAP - ch; left = rect.left + rect.width / 2 - cw / 2;
    } else {
      placement = 'bottom'; top = rect.bottom + GAP; left = rect.left + rect.width / 2 - cw / 2;
    }
    top = Math.max(M, Math.min(top, vh - ch - M));
    left = Math.max(M, Math.min(left, vw - cw - M));
    setPos({ top, left, placement });
  }, [rect, win, index]);

  const next = () => (isLast ? onClose() : setIndex(i => i + 1));
  const back = () => setIndex(i => Math.max(0, i - 1));

  // Spotlight geometry (padded around the target).
  const PAD = 8;
  const hole = rect
    ? { x: rect.left - PAD, y: rect.top - PAD, w: rect.width + PAD * 2, h: rect.height + PAD * 2 }
    : null;

  // Arrow offset along the card edge, aimed at the target's centre.
  let arrowStyle: React.CSSProperties | null = null;
  if (rect && pos.placement !== 'center') {
    const card = cardRef.current;
    const cw = card?.offsetWidth ?? 0;
    const ch = card?.offsetHeight ?? 0;
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    if (pos.placement === 'right') arrowStyle = { left: -5, top: Math.max(14, Math.min(cy - pos.top, ch - 14)) };
    else if (pos.placement === 'left') arrowStyle = { right: -5, top: Math.max(14, Math.min(cy - pos.top, ch - 14)) };
    else if (pos.placement === 'top') arrowStyle = { bottom: -5, left: Math.max(14, Math.min(cx - pos.left, cw - 14)) };
    else arrowStyle = { top: -5, left: Math.max(14, Math.min(cx - pos.left, cw - 14)) };
  }

  return (
    <div className="fixed inset-0 z-[200]" role="dialog" aria-modal="true">
      {/* Dimmed backdrop with a spotlight cutout */}
      <svg width={win.w} height={win.h} className="absolute inset-0 pointer-events-auto">
        <defs>
          <mask id="tour-mask">
            <rect x={0} y={0} width={win.w} height={win.h} fill="white" />
            {hole && (
              <rect x={hole.x} y={hole.y} width={hole.w} height={hole.h} rx={14} ry={14} fill="black" />
            )}
          </mask>
        </defs>
        <rect x={0} y={0} width={win.w} height={win.h} fill="rgba(15,23,42,0.72)" mask="url(#tour-mask)" />
      </svg>

      {/* Highlight ring around the target */}
      {hole && (
        <div
          className="absolute rounded-2xl ring-2 ring-teal-400 pointer-events-none transition-all duration-200"
          style={{ top: hole.y, left: hole.x, width: hole.w, height: hole.h }}
        />
      )}

      {/* Tooltip card */}
      <div
        ref={cardRef}
        className="absolute w-[min(340px,calc(100vw-24px))] bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 p-5 transition-all duration-200"
        style={{ top: pos.top, left: pos.left }}
      >
        {arrowStyle && (
          <div
            className="absolute w-2.5 h-2.5 rotate-45 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700"
            style={{
              ...arrowStyle,
              borderTopWidth: pos.placement === 'bottom' ? 1 : 0,
              borderLeftWidth: pos.placement === 'right' ? 1 : 0,
              borderBottomWidth: pos.placement === 'top' ? 1 : 0,
              borderRightWidth: pos.placement === 'left' ? 1 : 0,
            }}
          />
        )}

        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-xl bg-teal-500 text-white flex items-center justify-center shrink-0">
            <Icon as={step.NavIcon} size={18} />
          </div>
          <div className="min-w-0">
            <h3 className="font-serif font-bold text-slate-900 dark:text-slate-100 leading-tight">
              {t(`trip.tour.steps.${step.key}.title`)}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="ml-auto -mt-1 -mr-1 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors cursor-pointer"
            aria-label={t('common.close')}
          >
            <Icon as={X} size={16} />
          </button>
        </div>

        <p className="mt-2.5 text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
          {t(`trip.tour.steps.${step.key}.body`)}
        </p>

        {/* Progress dots */}
        <div className="mt-4 flex items-center gap-1.5">
          {steps.map((s, i) => (
            <span
              key={s.key}
              className={`h-1.5 rounded-full transition-all ${
                i === index ? 'w-4 bg-teal-500' : 'w-1.5 bg-slate-200 dark:bg-slate-700'
              }`}
            />
          ))}
        </div>

        <div className="mt-4 flex items-center justify-between gap-3">
          <button
            onClick={onClose}
            className="text-xs font-medium text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors cursor-pointer"
          >
            {t('trip.tour.skip')}
          </button>
          <div className="flex items-center gap-2">
            {index > 0 && (
              <button
                onClick={back}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
              >
                <Icon as={ArrowLeft} size={14} />
                {t('trip.tour.back')}
              </button>
            )}
            <button
              onClick={next}
              className="inline-flex items-center gap-1 px-3.5 py-1.5 rounded-lg text-sm font-semibold bg-teal-500 hover:bg-teal-600 text-white transition-colors cursor-pointer"
            >
              {isLast ? t('trip.tour.finish') : t('trip.tour.next')}
              {!isLast && <Icon as={ArrowRight} size={14} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
